import type { BookingPreferences, SelectedSlot } from "./bookingFlowMachine"
import { bookingFlowMachine } from "./bookingFlowMachine"
import { deriveConsentGate } from "./consentGate"
import {
  BookedAppointment,
  ConsentType,
  ScreeningAppointmentType,
  ScreeningProgram,
  useBeginBooking,
  useBookingContext,
  useConsentStatus,
  useScreeningQuestions
} from "./hooks"
import { BookedPage } from "./pages/BookedPage"
import { ConsentPage } from "./pages/ConsentPage"
import { HealthQuestionsPage } from "./pages/HealthQuestionsPage"
import { NoAvailabilityPage } from "./pages/NoAvailabilityPage"
import { NotEligiblePage } from "./pages/NotEligiblePage"
import { SelectPreferencesPage } from "./pages/SelectPreferencesPage"
import { Box, Progress } from "@carrotfertility/carotene-core"
import { useMachine } from "@xstate/react"
import React from "react"

type BookingFlowProps = {
  // The program the member is booking into — drives the screening question set.
  program: ScreeningProgram
  // Navigation is the host's concern — these come in as callbacks rather than the
  // package importing the router.
  onExit?: () => void
  onReturnToProviderResults?: () => void
  // Sends the member to Carrot Academy's care options from the not-eligible page.
  onExploreCareOptions?: () => void
}

// Resolves the eligibility gate and booking context before starting the machine, so the init
// eligibility gate and the `alreadyConsented` guard decide on real values rather than defaults.
export function BookingFlow({
  program,
  onExit,
  onReturnToProviderResults,
  onExploreCareOptions
}: BookingFlowProps): JSX.Element {
  const bookingContext = useBookingContext()
  const consentStatus = useConsentStatus()
  const eligibility = useBeginBooking(program)
  // Resolve the appointment type as soon as eligibility lands so the screening-question set is
  // fetched for the right offering (defaults to initial while eligibility loads; the gate below holds
  // until both settle, and the machine is keyed on appointmentType so it remounts if it changes).
  const appointmentType: ScreeningAppointmentType = eligibility.data?.appointmentType ?? "initial"
  const screeningQuestions = useScreeningQuestions(program, appointmentType)

  if (bookingContext.isLoading || consentStatus.isLoading || eligibility.isLoading || screeningQuestions.isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center" }}>
        <Progress />
      </Box>
    )
  }

  // The consent gate: `alreadySignedConsent` skips the consent step when every required consent is
  // satisfied (the annual telehealth one lapses yearly, so it can be false again); `outstandingConsents`
  // is what the consent screen records. On a consent fetch failure this shows the screen and treats
  // all consents as outstanding so the member can still sign — see deriveConsentGate.
  const { alreadySignedConsent, outstandingConsents } = deriveConsentGate(consentStatus.data)
  const member = bookingContext.data?.member
  const memberName = member ? [member.firstName, member.lastName].filter(Boolean).join(" ") || undefined : undefined

  // Fail open on an eligibility fetch failure: safe because bookAppointment re-enforces eligibility
  // server-side before the write (MEM-771), so a failed lookup can't quietly become a booking.
  const eligibilityResult = eligibility.data
  const knownDataExclusionMessageId =
    eligibilityResult && !eligibilityResult.eligible ? eligibilityResult.exclusionMessageId : null
  // Skip the health-questions step when this offering has none (e.g. a follow-up). Default to showing
  // it if the question set failed to load — fail toward the safety gate, never silently skip it.
  const hasScreeningQuestions = screeningQuestions.data ? screeningQuestions.data.length > 0 : true

  return (
    // `useMachine` reads its input only at actor creation, so key the machine on the inputs that
    // define which booking this is — a program switch (X→Y→X with Y cached, so no loading remount)
    // would otherwise keep a stale actor. `alreadySignedConsent` is intentionally excluded: it's
    // read once for the consent-skip guard, and keying on it would remount mid-flow on a consent refetch.
    <BookingMachine
      key={`${program}:${appointmentType}:${knownDataExclusionMessageId ?? ""}`}
      program={program}
      memberName={memberName}
      alreadySignedConsent={alreadySignedConsent}
      outstandingConsents={outstandingConsents}
      appointmentType={appointmentType}
      knownDataExclusionMessageId={knownDataExclusionMessageId}
      hasScreeningQuestions={hasScreeningQuestions}
      onExit={onExit}
      onReturnToProviderResults={onReturnToProviderResults}
      onExploreCareOptions={onExploreCareOptions}
    />
  )
}

function BookingMachine({
  program,
  memberName,
  alreadySignedConsent,
  outstandingConsents,
  appointmentType,
  knownDataExclusionMessageId,
  hasScreeningQuestions,
  onExit,
  onReturnToProviderResults,
  onExploreCareOptions
}: BookingFlowProps & {
  alreadySignedConsent: boolean
  outstandingConsents: ConsentType[]
  memberName?: string
  appointmentType: ScreeningAppointmentType
  knownDataExclusionMessageId: string | null
  hasScreeningQuestions: boolean
}): JSX.Element | null {
  const [state, send] = useMachine(bookingFlowMachine, {
    input: { alreadySignedConsent, program, appointmentType, knownDataExclusionMessageId, hasScreeningQuestions }
  })
  const onContinue = () => send({ type: "CONTINUE" })
  // Screener refusal → offers "back to questions"; booking-time (known-data) refusal → terminal.
  const onScreenerDisqualified = (exclusionMessageId: string) =>
    send({ type: "DISQUALIFIED", exclusionMessageId, fromScreener: true })
  const onBookingDisqualified = (exclusionMessageId: string) =>
    send({ type: "DISQUALIFIED", exclusionMessageId, fromScreener: false })
  const onBackToQuestions = () => send({ type: "BACK_TO_QUESTIONS" })
  const onConfirmBooking = (preferences: BookingPreferences, slot: SelectedSlot, appointment: BookedAppointment) =>
    send({ type: "CONFIRM_BOOKING", preferences, slot, appointment })

  if (state.matches("consent"))
    return (
      <ConsentPage
        outstandingConsents={outstandingConsents}
        onContinue={onContinue}
        onReturnToProviderResults={onReturnToProviderResults}
      />
    )
  if (state.matches("healthQuestions"))
    return (
      <HealthQuestionsPage
        program={program}
        appointmentType={state.context.appointmentType}
        onContinue={onContinue}
        onDisqualified={onScreenerDisqualified}
        onReturnToProviderResults={onReturnToProviderResults}
      />
    )
  if (state.matches({ seeAvailability: "preferences" }))
    return (
      <SelectPreferencesPage
        program={program}
        onConfirmBooking={onConfirmBooking}
        onDisqualified={onBookingDisqualified}
        onReturnToProviderResults={onReturnToProviderResults}
      />
    )
  if (state.matches("booked"))
    return (
      <BookedPage
        program={program}
        appointment={state.context.appointment}
        offeringId={state.context.preferences?.offeringId}
        memberName={memberName}
        onBackToHome={onExit}
      />
    )
  if (state.matches("notEligible"))
    return (
      <NotEligiblePage
        exclusionMessageId={state.context.exclusionMessageId}
        onExploreCareOptions={onExploreCareOptions}
        // Only offer "back to questions" for a screener disqualification — never a known-data refusal.
        onBackToQuestions={state.context.disqualifiedByScreener ? onBackToQuestions : undefined}
      />
    )
  if (state.matches("noAvailability")) return <NoAvailabilityPage onBackToHome={onExit} />
  return null
}
